import { GearSixIcon, MagnifyingGlassIcon, SquaresFourIcon, UserIcon } from "../../../shared/assets/icons";
import { LogoSvg } from "@/frontend/shared/assets/LogoSvg";
import { SquareButton } from "@/frontend/shared/ui/Button";
import { Typography } from "@/frontend/shared/ui/Typography/Typography";
import styles from "./InnoveraCompanyHeader.module.scss";
import { UsersAvatar } from "@/frontend/shared/ui/UsersAvatar/UsersAvatar";
import clsx from "clsx";
import { getRouteExpertOrganizationById, getRouteSettingsPage, getRouteSignIn } from "@/frontend/shared/const/router";
import { useHistoricalNavigate } from "@/frontend/shared/lib/useHistoricalNavigate/useHistoricalNavigate";
import { useMemo, useRef, useState } from "react";
import { useLocation } from "react-router";
import { EContent } from "../../DashboardHeader/types/types";
import { useMe } from "@/frontend/shared/lib/hooks/useMe";
import { DropshadowMenu } from "@/frontend/shared/ui/DropshadowMenu";
import { postLogout } from "../../InnoveraHeader/model/services/postLogout";
import { SearchInput } from "@/frontend/shared/ui/SearchInput";
import { useQuery } from "@tanstack/react-query";
import { postSearchCompanies } from "../model/services/postSearchCompanies";
import { reportErrorToSentry } from "@/frontend/shared/lib/reportErrorToSentry/reportErrorToSentry";
import { useDebounce } from "@/frontend/shared/lib/useDebounce/useDebounce";
import { CompaniesDropdown } from "./CompaniesDropdown/CompaniesDropdown";
import { useOutsideClick } from "@/frontend/shared/lib/hooks/useOutsideClick";
import { fireAnalyticsEvent } from "@/frontend/features/analytics";
import { EPostHogEventName } from "@/frontend/features/analytics/const/const";
import { useScreenWidth } from "@/frontend/shared/lib/hooks/useScreenWidth";
import { getTabsForUser } from "../model/services/getTabsForUser";
import { ERoleAccess } from "@/frontend/app/providers/router/config/routeConfig";
import { extractOrganizationId } from "@/frontend/shared/lib/extractOrganizationId/extractOrganizationId";
import { getOrganizations } from "@/frontend/pages/ExpertPortfolioCompanyProfilePage/model/services/getOrganizations";
import { snackbarConstructor } from "@/frontend/shared/ui/CustomNotifications/ui/Snackbar/snackbarConstructor";
import { TCompanyData } from "@/frontend/features/Company/model/types/types";

type TInnoveraBreadCrumbsHeaderProps = {
  className?: string;
  activeContent?: EContent;
  setActiveContent?: (value: EContent) => void;
};

export function InnoveraCompanyHeader({ className, activeContent, setActiveContent }: TInnoveraBreadCrumbsHeaderProps) {
  const { pathname } = useLocation();
  const { navigate } = useHistoricalNavigate();
  const [isHeaderMenuOpen, setIsHeaderMenuOpen] = useState(false);
  const [isSearchOpened, setIsSearchOpened] = useState(false);
  const [companyValue, setCompanyValue] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [isSearchShown, setIsSearchShown] = useState(false);
  const [isOrganizationMenuOpen, setIsOrganizationMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { derived, data: profileData } = useMe();
  const userType = profileData?.entity;

  const isExpert = userType === ERoleAccess.EXPERT;
  const { data: organizations, isLoading } = useQuery({
    queryKey: ["expert/organizations"],
    queryFn: getOrganizations,
    enabled: isExpert,
    initialData: []
  });

  const currentOrganizationId = extractOrganizationId(pathname) || organizations?.[0]?.id;
  function changeOrganization(id: number) {
    setIsOrganizationMenuOpen(false);
    navigate(getRouteExpertOrganizationById(id));
  }

  const debouncedSearch = useDebounce((value) => {
    setCompanyValue(value);
    if (value.trim() !== "") {
      fireAnalyticsEvent(EPostHogEventName.HEADER_SEARCH_COMPANIES);
    }
  }, 400);

  function handleInputChange(value: string) {
    setInputValue(value);
    debouncedSearch(value);
  }

  useOutsideClick(dropdownRef, () => {
    if (isSearchOpened) {
      setIsSearchOpened(false);
    }
  });

  const {
    data: searchedCompanies,
    isFetching: isFetchingSearchedCompanies,
    refetch: refetchSearchedCompanies
  } = useQuery({
    queryKey: ["headersSearchedCompanies", companyValue],
    queryFn: () =>
      postSearchCompanies({
        name: companyValue,
        page: 1,
        limit: 5
      }),
    refetchOnWindowFocus: false,
    throwOnError: (error) => {
      console.error(error);
      snackbarConstructor("Error fetching companies", "error");
      reportErrorToSentry(error, "HEADER_COMPANIES: /search/companies");
      return false;
    }
  });

  const formattedSearchedCompanies = useMemo(() => {
    return (
      searchedCompanies?.companies?.slice(0, 5)?.map((company: TCompanyData) => ({
        id: company.id,
        name: company.name,
        headline: company.headline,
        logoUrl: company.logoUrl
      })) || []
    );
  }, [searchedCompanies]);

  async function logout() {
    await postLogout();
    localStorage.clear();
    navigate(getRouteSignIn());
  }

  function onKeyDownEnter(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter") {
      refetchSearchedCompanies();
      setIsSearchOpened(true);
    }
  }

  const isMobile = useScreenWidth() <= 768;

  const handleMobileSearchClose = () => {
    setIsSearchOpened(false);
    setIsSearchShown(false);
    setInputValue("");
  };

  const tabs = getTabsForUser(userType, currentOrganizationId, setActiveContent, activeContent);

  if (isMobile && isSearchShown) {
    return (
      <div className={clsx(styles.mobileContainer, className)}>
        <div className={clsx(styles.searchContainer, styles.mobile)} ref={dropdownRef}>
          <SearchInput
            value={inputValue}
            onChange={(e) => handleInputChange(e)}
            placeholder="Search"
            className={clsx(styles.searchInput, styles.mobile)}
            onClick={(e) => {
              e.stopPropagation();
              setIsSearchOpened(true);
            }}
            onKeyDown={onKeyDownEnter}
            onClose={handleMobileSearchClose}
            inputId="innoveraHeader-searchInput"
          />
          <CompaniesDropdown
            className={clsx(styles.companiesContainer, styles.mobile)}
            inputValue={inputValue}
            options={formattedSearchedCompanies}
            isOpen={isSearchOpened}
            setInputValue={setInputValue}
            onClose={() => setIsSearchOpened(false)}
            isCompaniesLoading={isFetchingSearchedCompanies}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={clsx(styles.container, className)}>
      <LogoSvg
        className={styles.logoSvg}
        size={isMobile ? "small" : "normal"}
        onClick={tabs?.[0]?.goTo}
        id="innoveraHeader-logo"
      />
      {!!tabs && (
        <div className={styles.buttonsContainer}>
          {tabs.map((tab, index) => (
            <Typography
              key={index}
              variant="label-4"
              color="greyscale500"
              className={clsx(styles.switchPageButton, tab.isActive && styles.active)}
              onClick={tab.goTo}
              id={tab.id}
            >
              {tab.icon}
              <span className={styles.switchPageButtonText}>{tab.label}</span>
            </Typography>
          ))}
        </div>
      )}
      <div>
        <div className={styles.rightSideContainer}>
          {!isMobile && (
            <div className={styles.searchContainer} ref={dropdownRef}>
              <SearchInput
                value={inputValue}
                onChange={(e) => handleInputChange(e)}
                placeholder="Search"
                className={styles.searchInput}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsSearchOpened(true);
                }}
                onKeyDown={onKeyDownEnter}
                inputId="innoveraHeader-searchInput"
              />
              <CompaniesDropdown
                className={styles.companiesContainer}
                inputValue={inputValue}
                options={formattedSearchedCompanies}
                isOpen={isSearchOpened}
                setInputValue={setInputValue}
                onClose={() => setIsSearchOpened(false)}
                isCompaniesLoading={isFetchingSearchedCompanies}
              />
            </div>
          )}
          <div className={styles.iconsContainer}>
            <SquareButton
              size="large"
              variant="tertiary"
              onClick={() => setIsSearchShown(true)}
              className={styles.mobile}
            >
              <MagnifyingGlassIcon />
            </SquareButton>
            {userType === ERoleAccess.INVESTOR && (
              <SquareButton
                size="large"
                variant="tertiary"
                onClick={() => navigate(getRouteSettingsPage())}
                id="innoveraHeader-settingsMenuButton"
              >
                <GearSixIcon />
              </SquareButton>
            )}
          </div>
          <div className={styles.avatarContainer}>
            <UsersAvatar
              id="innoveraHeader-avatar"
              className={styles.avatar}
              nameInitials={derived.nameInitials}
              profileImg={derived.profileImageUrl}
              organizationId={profileData?.organizationId}
              onClick={() => {
                setIsHeaderMenuOpen((prev) => !prev);
              }}
            />
            <DropshadowMenu
              isOpen={isHeaderMenuOpen}
              onClose={() => setIsHeaderMenuOpen(false)}
              options={[
                <div
                  className={styles.dropshadowOption}
                  onClick={() => {
                    logout();
                  }}
                  id="innoveraHeader-logoutButton"
                >
                  <UserIcon /> Logout
                </div>
              ]}
              top="30px"
              right="0px"
            />
          </div>
          {isExpert && (
            <>
              <div className={styles.organizationContainer}>
                <div className={styles.iconsContainer}>
                  <SquareButton
                    size="large"
                    variant="tertiary"
                    onClick={() => setIsOrganizationMenuOpen((prev) => !prev)}
                  >
                    <SquaresFourIcon />
                  </SquareButton>
                </div>

                <DropshadowMenu
                  isOpen={isOrganizationMenuOpen}
                  onClose={() => setIsOrganizationMenuOpen(false)}
                  className={styles.organizationMenu}
                  options={organizations.map((org) => {
                    return (
                      <div
                        key={org.id}
                        className={styles.organizationOption}
                        onClick={() => changeOrganization?.(org.id)}
                      >
                        <Typography variant="label-4" color="greyscale800" className={styles.organizationName}>
                          {org.name || "-"}
                        </Typography>
                      </div>
                    );
                  })}
                  top="62px"
                  right="0px"
                />
              </div>
              <Typography variant="label-4" color="greyscale600" style={{ textWrap: "nowrap" }}>
                {organizations.find((org) => org.id === currentOrganizationId)?.name || "-"}
              </Typography>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
